import { FC, useEffect, useId, useMemo, useRef, useState } from "react"

import { Button, Stack } from "@mui/material"
import { ITeacherStorePurchaseResult } from "api/reactQuery/mutations/classesStorePurchase.types"
import { useQueryClassesStoreItems } from "api/reactQuery/queries/classesStoreItems"
import Dialog from "components/common/dialog/dialog"
import { useClassId } from "hooks/navigation"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"

import DoneStep from "./doneStep"
import ReviewStep from "./reviewStep"
import SelectStep from "./selectStep"
import { IProps } from "./SendStoreChargeDialog.types"
import {
  addToCart,
  countAvailableItems,
  decrementInCart,
  filterStoreItems,
  getCartCostPerStudent,
  ICartLine,
  removeFromCart,
} from "./SendStoreChargeDialog.utils"
import StepFooter, { CartSummary } from "./stepFooter"
import { useStoreChargePurchase } from "./useStoreChargePurchase"
import { useStoreChargeReview } from "./useStoreChargeReview"

type Step = "select" | "review"

const SendStoreChargeDialog: FC<IProps> = ({
  isOpen,
  handleClose,
  onCharged,
  entry,
  source = "teacher",
  students,
  isWholeClass = false,
  excludedStudentIds = [],
  excludedStudents = [],
  wholeClassCount,
}) => {
  const { t } = useTranslation()
  const classId = Number(useClassId())
  const balanceMessageId = useId()
  const stockMessageId = useId()
  const rosterErrorId = useId()

  // Whole-class `students` is only the loaded table page; the real count comes from the parent.
  const studentCount =
    isWholeClass && wholeClassCount != null ? wholeClassCount : students.length

  const [step, setStep] = useState<Step>("select")
  const [cart, setCart] = useState<ICartLine[]>([])
  const [result, setResult] = useState<ITeacherStorePurchaseResult | null>(null)
  const [search, setSearch] = useState("")
  // True after any charge request — closing after a failed submit is not a Mixpanel cancel.
  const attemptedRef = useRef(false)
  const conflictBannerRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLElement>(null)
  const [showConflictBanner, setShowConflictBanner] = useState(false)
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false)
  // Items `dataUpdatedAt` when an unreadable 409 invalidated stock; Review waits for newer data.
  const [staleStockAt, setStaleStockAt] = useState<number | null>(null)

  const {
    data: itemsResponse,
    isLoading: isLoadingItems,
    isError: isItemsError,
    refetch: refetchItems,
    dataUpdatedAt: itemsUpdatedAt,
  } = useQueryClassesStoreItems({ classId, options: { enabled: isOpen } })
  const isStockRefreshing =
    staleStockAt !== null && itemsUpdatedAt <= staleStockAt && !isItemsError
  const items = useMemo(() => itemsResponse?.data ?? [], [itemsResponse])
  const itemsReady = !isLoadingItems && !isItemsError

  const filteredItems = useMemo(
    () => filterStoreItems(items, search),
    [items, search],
  )
  const availableCount = countAvailableItems(filteredItems)
  // Screen-reader mirror of the count, debounced so typing isn't announced per key.
  const [announcedCount, setAnnouncedCount] = useState<number | null>(null)
  useEffect(() => {
    if (!itemsReady) return
    const timeout = setTimeout(() => setAnnouncedCount(availableCount), 500)
    return () => clearTimeout(timeout)
  }, [availableCount, itemsReady])

  // Open focuses the search field; every later step / result change focuses the heading so it's announced.
  // Compared to the last focused step, not a first-run flag, so StrictMode's double effect is a no-op.
  const stepKey = result ? "done" : step
  const focusedStepKeyRef = useRef(stepKey)
  useEffect(() => {
    if (focusedStepKeyRef.current === stepKey) return
    focusedStepKeyRef.current = stepKey
    titleRef.current?.focus()
  }, [stepKey])

  // Focus reads the banner once; no role="alert" so it isn't announced twice.
  useEffect(() => {
    if (showConflictBanner) conflictBannerRef.current?.focus()
  }, [showConflictBanner])

  const costPerStudent = getCartCostPerStudent(cart)

  const {
    isRosterFallback,
    isRosterPending,
    isRosterFailed,
    refetchRoster,
    balanceCheck,
    predictedChargedCount,
    predictedSkippedCount,
    noneCanAfford,
    isStockPending,
    lineStocks,
    shortLineCount,
    isStockBlocking,
    isChargeBlocked,
  } = useStoreChargeReview({
    isOpen,
    isReviewStep: step === "review",
    classId,
    source,
    students,
    isWholeClass,
    excludedStudentIds,
    studentCount,
    cart,
    costPerStudent,
    items,
    isStockRefreshing,
  })
  const chargeBlockerId = isRosterFailed
    ? rosterErrorId
    : noneCanAfford
      ? balanceMessageId
      : isStockBlocking
        ? stockMessageId
        : undefined

  const { charge, isSubmitting } = useStoreChargePurchase({
    classId,
    entry,
    cart,
    students,
    isWholeClass,
    excludedStudentIds,
    studentCount,
    onSuccess: setResult,
    onStockConflict: (isReadable) => {
      if (!isReadable) setStaleStockAt(itemsUpdatedAt)
      setShowConflictBanner(true)
    },
  })

  const onCancel = () => {
    if (!attemptedRef.current) {
      mixpanel.track("send_store_charge", { action: "cancel", step })
    }
    handleClose()
  }

  const onDone = () => {
    onCharged?.(result?.charged_student_ids ?? [])
    handleClose()
  }
  // Single entry for close icon / Cancel / Esc; the backdrop never closes (cart would be lost on a stray click).
  const requestClose = () => {
    if (isSubmitting) return
    if (result) return onDone()
    if (cart.length > 0 || step === "review") return setIsConfirmCloseOpen(true)
    onCancel()
  }

  const submit = () => {
    if (cart.length === 0 || studentCount === 0 || isChargeBlocked) return
    attemptedRef.current = true
    setShowConflictBanner(false)
    charge()
  }

  const chargedCount = result?.charged_student_ids.length ?? 0

  const title = result
    ? chargedCount > 0
      ? t("storeCharge.done.dialogTitle")
      : t("storeCharge.done.noneTitle")
    : step === "select"
      ? t("storeCharge.select.title")
      : t("storeCharge.review.title")
  const subtitle = result
    ? undefined
    : step === "select"
      ? t("storeCharge.select.subtitle")
      : t("storeCharge.review.subtitle")

  const selectStep = (
    <SelectStep
      studentCount={studentCount}
      search={search}
      onSearchChange={setSearch}
      announcedCount={announcedCount}
      availableCount={availableCount}
      items={items}
      filteredItems={filteredItems}
      isLoadingItems={isLoadingItems}
      isItemsError={isItemsError}
      onRetryItems={() => refetchItems()}
      cart={cart}
      onAdd={(item) => setCart((prev) => addToCart(prev, item))}
      onDecrement={(itemId) => setCart((prev) => decrementInCart(prev, itemId))}
      onRemove={(itemId) => setCart((prev) => removeFromCart(prev, itemId))}
    />
  )

  const reviewStep = (
    <ReviewStep
      showConflictBanner={showConflictBanner}
      conflictBannerRef={conflictBannerRef}
      studentCount={studentCount}
      excludedStudentIds={excludedStudentIds}
      excludedStudents={excludedStudents}
      cart={cart}
      costPerStudent={costPerStudent}
      isRosterFallback={isRosterFallback}
      isRosterPending={isRosterPending}
      isRosterFailed={isRosterFailed}
      onRetryRoster={() => refetchRoster()}
      balanceCheck={balanceCheck}
      predictedChargedCount={predictedChargedCount}
      predictedSkippedCount={predictedSkippedCount}
      noneCanAfford={noneCanAfford}
      isStockPending={isStockPending}
      lineStocks={lineStocks}
      shortLineCount={shortLineCount}
      isStockBlocking={isStockBlocking}
      balanceMessageId={balanceMessageId}
      stockMessageId={stockMessageId}
      rosterErrorId={rosterErrorId}
    />
  )

  const doneStep = result && (
    <DoneStep result={result} costPerStudent={costPerStudent} />
  )

  const footer = (
    <StepFooter>
      {result ? (
        <Button sx={{ ml: "auto" }} onClick={onDone}>
          {t("storeCharge.done.close")}
        </Button>
      ) : step === "select" ? (
        <>
          <CartSummary
            lineCount={cart.length}
            studentCount={studentCount}
            costPerStudent={costPerStudent}
          />
          <Button variant="outlined" onClick={requestClose}>
            {t("storeCharge.cancel")}
          </Button>
          <Button
            disabled={cart.length === 0 || studentCount === 0}
            onClick={() => setStep("review")}
          >
            {t("storeCharge.select.reviewAction")}
          </Button>
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            disabled={isSubmitting}
            onClick={() => {
              setShowConflictBanner(false)
              setStep("select")
            }}
          >
            {t("storeCharge.review.backAction")}
          </Button>
          <Button
            loading={isSubmitting}
            aria-busy={isSubmitting}
            aria-disabled={isChargeBlocked || undefined}
            aria-describedby={chargeBlockerId}
            onClick={submit}
            sx={(theme) => ({
              ml: "auto",
              "&[aria-disabled='true']": {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.common.white,
                cursor: "not-allowed",
                ":hover": { backgroundColor: theme.palette.primary.light },
              },
            })}
          >
            {t("storeCharge.review.chargeAction", {
              count: predictedChargedCount,
            })}
          </Button>
        </>
      )}
    </StepFooter>
  )

  return (
    <>
      <Dialog
        titleText={title}
        titleRef={titleRef}
        descriptionText={subtitle}
        descriptionTextProps={{ variant: "subtitle1" }}
        open={isOpen}
        onClose={(_, reason) => {
          if (reason === "escapeKeyDown") requestClose()
        }}
        onCloseIconClick={requestClose}
        desktopMaxWidth="867px"
        tabletMaxWidth="720px"
        customActionButtonsSlot={null}
      >
        {result ? doneStep : step === "select" ? selectStep : reviewStep}
        {footer}
      </Dialog>
      <Dialog
        titleText={t("storeCharge.confirmClose.title")}
        descriptionText={t("storeCharge.confirmClose.body")}
        descriptionTextProps={{ variant: "subtitle1" }}
        open={isConfirmCloseOpen}
        onClose={() => setIsConfirmCloseOpen(false)}
        desktopMaxWidth="440px"
        tabletMaxWidth="440px"
        customActionButtonsSlot={null}
      >
        <Stack direction="row" justifyContent="flex-end" gap={1.25}>
          <Button
            variant="outlined"
            autoFocus
            onClick={() => setIsConfirmCloseOpen(false)}
          >
            {t("storeCharge.confirmClose.keep")}
          </Button>
          <Button
            onClick={() => {
              setIsConfirmCloseOpen(false)
              onCancel()
            }}
          >
            {t("storeCharge.confirmClose.discard")}
          </Button>
        </Stack>
      </Dialog>
    </>
  )
}

export default SendStoreChargeDialog
